import { useEffect } from "react";
import { useSelector } from "react-redux";

import { getLocale } from "@gamedev-web/gamedev-sdk-core";

import { isBlockedCountryIso } from "../constants/blockedCountries";
import { getXboxOnboardData } from "../redux/selectors/xboxOnboardSelectors";

import { identifyClarityUser, setClarityTags } from "./clarityClient";
import { ClarityTags } from "./clarityEvents";

/**
 * Attaches partner identity and segmentation tags to the Clarity session.
 *
 * Runs from `ClarityInitializer`, which is mounted in both the client and the
 * server entry points, so everything here is a no-op during SSR and whenever
 * the visitor has not consented to analytics cookies.
 *
 * Anonymous visitors (the pre-auth landing page) have no identifier at all —
 * `identify` is skipped entirely rather than being sent a placeholder value,
 * which would collapse every anonymous session into a single fake partner.
 */
export const useClarityIdentity = () => {
  const locale = useSelector(getLocale) as string | undefined;
  const onboardData = useSelector(getXboxOnboardData);

  const accountId = onboardData?.accountId;
  const studioName = onboardData?.studio?.studioName;
  const countryIsoCode = onboardData?.countryIsoCode;

  useEffect(() => {
    // Clarity's documentation specifies identify should be called per page
    // rather than once per session, so this deliberately re-runs on navigation.
    identifyClarityUser(accountId, studioName);
  }, [accountId, studioName]);

  useEffect(() => {
    setClarityTags({
      [ClarityTags.locale]: locale,
    });
  }, [locale]);

  useEffect(() => {
    if (!onboardData) {
      return;
    }

    setClarityTags({
      [ClarityTags.applicationStatus]: onboardData.status,
      [ClarityTags.accountType]: onboardData.accountType,
      [ClarityTags.pcEnrollmentStatus]: onboardData.partnerCenterEnrollmentStatus,
      [ClarityTags.countryOrRegion]: countryIsoCode,
      [ClarityTags.isBlockedCountry]: isBlockedCountryIso(countryIsoCode),
      [ClarityTags.isEmbargoedCountry]: onboardData.isEmbargoedCountry,
      [ClarityTags.isFastTrackAccount]: onboardData.isFastTrackAccount,
      [ClarityTags.hasSubmittedGameConcept]: onboardData.hasSubmittedGameConcept,
      [ClarityTags.hasApprovedGameConcept]: onboardData.hasApprovedGameConcept,
      [ClarityTags.sellerId]: onboardData.sellerID,
    });
  }, [onboardData, countryIsoCode]);
};
